import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuthContext } from '../AuthContext/AuthContext';
import { getUserMenuList, getAllMenuTypes, getMenuTypePathSegment, getStadiumMaxBannerQuantity, getStadiumMaxRecommendQuantity } from './userMenu.services';
import { doc, writeBatch, setDoc } from '../../libs/firestore-wrapper';
import { ICategoryDetails } from '../../pages/MenuManagement/menu.types';
import { useCategoriesContext } from '../CategoriesContext';
import db_config from '../../configs/db_config';
import { db } from '../../libs/firebase';
import { Food, FoodItem, MenuType, UserMenuListData, UserMenuRecommendedFood } from '../../pages/UserMenus/userMenu.types';

export interface UserMenuCategory extends ICategoryDetails {
  foods: Food[];
}

interface UserMenuContextValue {
  categories: UserMenuCategory[];
  getFoodsByCategoryId: (categoryId: string) => Food[];
  getFoodById: (foodId: string) => Food | undefined;
  loading: boolean;
  refetch: () => void;
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string) => void;
  activeMenuType: MenuType | null;
  setActiveMenuType: (menuType: MenuType | null) => void;
  updateCategoryOrder: (categories: ICategoryDetails[]) => Promise<void>;
  updateUserMenuCategory: (categoryId: string, foodItems: FoodItem[], recommendationsMap: Record<string, FoodItem[]>) => Promise<void>;
  maxBannerQuantity: number | null;
  maxRecommendQuantity: number | null;
}

const UserMenuContext = createContext<UserMenuContextValue>({
  categories: [],
  getFoodsByCategoryId: () => [],
  getFoodById: () => undefined,
  loading: true,
  refetch: () => { },
  activeCategoryId: null,
  setActiveCategoryId: () => { },
  activeMenuType: null,
  setActiveMenuType: () => { },
  updateCategoryOrder: async () => { },
  updateUserMenuCategory: async () => { },
  maxBannerQuantity: null,
  maxRecommendQuantity: null,
});

export const UserMenuProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<UserMenuCategory[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [activeMenuType, setActiveMenuType] = useState<MenuType | null>(null);
  const [maxBannerQuantity, setMaxBannerQuantity] = useState<number | null>(null);
  const [maxRecommendQuantity, setMaxRecommendQuantity] = useState<number | null>(null);
  const { userInfo } = useAuthContext();
  const { foodMenu: menuData, loading: menuLoading } = useCategoriesContext();

  const fetchData = useCallback(async () => {
    const stadiumId = userInfo?.stadium_id;
    if (!stadiumId || menuLoading) {
      return;
    }

    try {
      setLoading(true);

      const menuTypes = getAllMenuTypes();

      const [userMenuListsData, maxBannerQty, maxRecommendQty] = await Promise.all([
        Promise.all(menuTypes.map((menuType) => getUserMenuList(stadiumId, menuType))),
        getStadiumMaxBannerQuantity(stadiumId),
        getStadiumMaxRecommendQuantity(stadiumId)
      ]);

      setMaxBannerQuantity(maxBannerQty);
      setMaxRecommendQuantity(maxRecommendQty);

      const listsMap = Object.fromEntries(menuTypes.map((menuType, index) => [menuType, userMenuListsData[index]])) as Record<MenuType, UserMenuListData>;

      const currentMenuType = activeMenuType ?? menuTypes[0];
      if (!activeMenuType) {
        setActiveMenuType(currentMenuType);
      }

      const userMenuListData = listsMap[currentMenuType];

      const userCategories = userMenuListData?.categories ?? [];
      const sortedUserCategories = [...userCategories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      const categoryIds = sortedUserCategories.filter((uc) => uc && uc.id).map((uc) => uc.id);

      const categoryMap = new Map(menuData.categories.map((cat) => [cat.id, cat]));

      const categoryFoodMap = new Map<
        string,
        Map<
          string,
          {
            banner: boolean;
            banner_order?: number;
            enabled: boolean;
            order: number;
            recommended_food?: UserMenuRecommendedFood[];
          }
        >
      >();

      sortedUserCategories.forEach((category) => {
        if (!category?.id) return;

        const foodPropsMap = new Map<
          string,
          {
            banner: boolean;
            banner_order?: number;
            enabled: boolean;
            order: number;
            recommended_food?: UserMenuRecommendedFood[];
          }
        >();

        category?.food_ids?.forEach((foodItem) => {
          foodPropsMap.set(foodItem.id, {
            banner: foodItem.banner,
            banner_order: foodItem.banner_order,
            enabled: foodItem.enabled,
            order: foodItem.order,
            recommended_food: foodItem.recommended_food,
          });
        });

        categoryFoodMap.set(category.id, foodPropsMap);
      });

      const allCategories: UserMenuCategory[] = categoryIds
        .map((catId) => {
          const baseCat = categoryMap.get(catId);
          if (!baseCat) return undefined;

          const foodPropsMap = categoryFoodMap.get(catId);
          if (!foodPropsMap) {
            return {
              ...baseCat,
              order: categoryIds.indexOf(catId),
              foods: [],
            } as UserMenuCategory;
          }

          const isPreOrderMenuType =
            currentMenuType === MenuType.REGULAR_PRE_ORDERS ||
            currentMenuType === MenuType.SPECIAL_PRE_ORDERS;

          const categoryFoods = menuData.foods
            .map((food) => {
              const menuFood = foodPropsMap.get(food.id);
              if (!menuFood) return null;

              // For pre-order menus, only show foods with pre-order explicitly enabled
              if (isPreOrderMenuType && !food.enabled_pre_order) return null;

              // For regular/special menus, hide foods with available toggled off
              if (!isPreOrderMenuType && food.enabled === false) return null;

              return {
                ...food,
                food_enabled: food.enabled,
                ...menuFood,
                enabled: menuFood.enabled,
                recommended_food: menuFood.recommended_food?.map((rf) => {
                  const recommendedFoodData = menuData.foods.find((f) => f.id === rf.id);
                  return {
                    enabled: rf.enabled,
                    order: rf.order,
                    recommended_food_id: rf.id,
                    food_enabled: recommendedFoodData?.enabled ?? false,
                  };
                }),
              } as unknown as Food;
            })
            .filter((f): f is Food => f !== null)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

          return {
            ...baseCat,
            order: categoryIds.indexOf(catId),
            foods: categoryFoods,
          } as UserMenuCategory;
        })
        .filter((cat): cat is UserMenuCategory => cat !== undefined);

      setCategories(allCategories);

      if (allCategories.length > 0) {
        setActiveCategoryId((prev) => prev ?? allCategories[0].id);
      }
    } catch (error) {
      console.error('UserMenuContext: Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [userInfo, activeMenuType, menuData, menuLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getFoodsByCategoryId = useCallback(
    (categoryId: string): Food[] => {
      const category = categories.find((c) => c.id === categoryId);
      return category?.foods ?? [];
    },
    [categories]
  );

  const getFoodById = useCallback(
    (foodId: string): Food | undefined => {
      for (const category of categories) {
        const food = category.foods.find((f) => f.id === foodId);
        if (food) return food;
      }
      return undefined;
    },
    [categories]
  );

  const updateCategoryOrder = useCallback(
    async (newCategories: ICategoryDetails[]) => {
      const stadiumId = userInfo?.stadium_id;
      if (!stadiumId || !activeMenuType) {
        throw new Error('Stadium ID or Menu Type not available');
      }

      try {
        const menuTypePath = getMenuTypePathSegment(activeMenuType);
        const batch = writeBatch();

        newCategories.forEach((category, index) => {
          const categoryRef = doc(db, `${db_config.DB_TABLE_USER_MENU_LIST}/${stadiumId}/${menuTypePath}/${category.id}`);
          batch.set(categoryRef, { order: index }, { merge: true });
        });

        await batch.commit();
      } catch (error) {
        console.error('Error updating category order:', error);
        throw error;
      }
    },
    [userInfo, activeMenuType, fetchData]
  );

  const updateUserMenuCategory = useCallback(
    async (categoryId: string, foodItems: FoodItem[], recommendationsMap: Record<string, FoodItem[]>) => {
      const stadiumId = userInfo?.stadium_id;
      if (!stadiumId || !activeMenuType) {
        throw new Error('Stadium ID or Menu Type not available');
      }

      try {
        const menuTypePath = getMenuTypePathSegment(activeMenuType);
        const categoryRef = doc(db, `${db_config.DB_TABLE_USER_MENU_LIST}/${stadiumId}/${menuTypePath}/${categoryId}`);

        const updatedFoodIds = foodItems.map((item, index) => {
          const recommendations = recommendationsMap[item.id] || [];
          return {
            id: item.id,
            banner: !!item.banner,
            banner_order: item.banner_order ?? 0,
            enabled: !!item.enabled,
            order: index,
            recommended_food: recommendations.map((rec: FoodItem, recIndex: number) => ({
              id: rec.id,
              enabled: !!rec.enabled,
              order: recIndex,
            })),
          };
        });

        await setDoc(categoryRef, { food_ids: updatedFoodIds }, { merge: true });

        await fetchData();
      } catch (error) {
        console.error('Error updating user menu category:', error);
        throw error;
      }
    },
    [userInfo, activeMenuType, fetchData]
  );

  return (
    <UserMenuContext.Provider
      value={{
        categories,
        getFoodsByCategoryId,
        getFoodById,
        loading,
        refetch: fetchData,
        activeCategoryId,
        setActiveCategoryId,
        activeMenuType,
        setActiveMenuType,
        updateCategoryOrder,
        updateUserMenuCategory,
        maxBannerQuantity,
        maxRecommendQuantity,
      }}
    >
      {children}
    </UserMenuContext.Provider>
  );
};

export const useUserMenuContext = () => {
  const context = useContext(UserMenuContext);
  return context;
};
